import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * A count on a control, in the shape iOS taught everybody to read.
 *
 * A red disc with white digits, a true circle at one digit and a capsule
 * beyond, sitting on the corner of the thing it counts. A reader does not
 * parse it, they recognise it — which is the whole value of borrowing a
 * convention rather than inventing one, and why the details below are not
 * arbitrary. Ported from PropPay LX, where it replaced three different discs
 * for one count.
 *
 * ── The two tones ──
 *
 * `alert` is the red disc (`--badge`, deliberately not `--destructive`): a
 * count somebody is meant to go and deal with — unread notifications, tasks
 * overdue. `quiet` is the same object in grey: how much is behind a control,
 * which is information rather than a summons. A row spends its one accent on
 * its primary action, and a second warm mark beside it reads as a second
 * call to action — which "3 documents" is not.
 *
 * `alert` belongs to ONE control: the notification bell in the header
 * (Andrew, 2026-11-02: "the red badges should not be used inline in the app.
 * Only on the notification bell"). Every other count, a summons or not, is
 * `quiet`, like the menu's own counts. tests/redBadgeOnlyOnBell.test.ts.
 *
 * ── The two variants ──
 *
 * They differ in ONE thing: whether the caller positions it. `overlay` hangs
 * off a glyph; `inline` sits in a row's flow beside a label.
 *
 * An overlay is anchored by its LEFT edge at the glyph's right edge
 * (`left-full`), and nudged from there with a negative margin. A badge grows
 * sideways when the count reaches two digits, and an element pinned by its
 * right edge grows LEFTWARD — so "99+" crawls back across the glyph it is
 * counting. Anchored on the left it grows away into open space. Measuring
 * from the glyph rather than the button around it also means one offset lands
 * correctly on an 18px, 19px or 20px icon.
 *
 * There is NO ring. A badge on a phone is a plain disc with a soft shadow
 * under it; a 2px surface-coloured ring draws a hard cut-out line that no
 * platform badge has.
 */

/** Where the digits stop growing. Past this the disc would stretch the row. */
const CAP = 99;

export function NotificationBadge({
  count,
  /**
   * What is being counted, for a screen reader.
   *
   * Required, because a bare number announced on its own tells somebody using
   * one nothing — "seven" is not a notification count until something says
   * so. Pass the singular; the plural is made here.
   */
  noun,
  variant = "overlay",
  tone = "alert",
  /**
   * The count is already in the accessible name of whatever this sits on.
   *
   * True for the bell, whose button announces "Notifications — 7 unread": a
   * second announcement from inside it is the same fact twice.
   */
  decorative,
  /** The plural, where adding "s" to `noun` is wrong (or in another language). */
  nounPlural,
  className,
}: {
  count: number;
  noun: string;
  nounPlural?: string;
  variant?: "overlay" | "inline";
  tone?: "alert" | "quiet";
  decorative?: boolean;
  className?: string;
}) {
  const t = useTranslation();
  // Zero is not a badge. A disc reading 0 is a control claiming attention it
  // has no reason to want.
  if (!count || count < 0) return null;

  return (
    <span
      {...(decorative
        ? { "aria-hidden": true as const }
        : { "aria-label": t("uiBadgeCount", "{count} {noun}", { count, noun: count === 1 ? noun : (nounPlural ?? `${noun}s`) }) })}
      className={cn(
        // 18px is the floor AND the width at one digit, which is what makes a
        // single count a circle rather than a slightly-wide lozenge.
        "flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1",
        // 12px bold in an 18px disc puts the digit's cap height at roughly half
        // the diameter — the proportion that makes a badge read as a badge
        // rather than a dot with something in it. `leading-none` because the
        // disc does the centring.
        "text-caption font-bold leading-none tabular-nums",
        tone === "alert"
          ? // The shadow, not a ring: it sits ON the surface and casts.
            "bg-badge text-badge-foreground shadow-[0_1px_3px_rgb(0_0_0/0.32)]"
          : "bg-foreground/10 text-muted-foreground",
        variant === "overlay" ? "absolute left-full" : "relative",
        className,
      )}
    >
      {count > CAP ? `${CAP}+` : count}
    </span>
  );
}
